"use client";

import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getStoredAuth } from "../utils/authStorage";
import axiosInstance from "../utils/axiosInstance";
import { API_ENDPOINTS } from "../constants/config";


function RequirePermission({ permission, children }) {
  const location = useLocation();
  const [{ token, user: storedUser }] = useState(() => getStoredAuth());
  const [user, setUser] = useState(storedUser || null);
  const [checking, setChecking] = useState(
    !!(token && storedUser?.role?.toLowerCase?.() === "staff")
  );

  useEffect(() => {
    let mounted = true;
    const role = storedUser?.role?.toLowerCase?.();

    if (!token || role !== "staff") {
      setUser(storedUser || null);
      return;
    }

    setChecking(true);
    axiosInstance
      .get(API_ENDPOINTS.usersProfile)
      .then((res) => {
        const fresh = res?.data?.data?.user;
        if (!mounted || !fresh) return;
        const merged = {
          ...(storedUser || {}),
          ...fresh,
          permissions: Array.isArray(fresh.permissions) ? fresh.permissions : [],
          roleType: fresh.roleType,
        };
        setUser(merged);
        localStorage.setItem("user", JSON.stringify(merged));
      })
      .catch(() => {
        if (mounted) setUser(storedUser || null);
      })
      .finally(() => {
        if (mounted) setChecking(false);
      });

    return () => {
      mounted = false;
    };
  }, [token, storedUser]);

  const role = user?.role?.toLowerCase?.();
  if (role === "superadmin" || role === "schooladmin" || role === "superchooladmin") {
    return children;
  }

  const normalize = (value) => (typeof value === "string" ? value.trim().toLowerCase() : "");
  const normalizedRequired = normalize(permission);
  const normalizedRoleType = normalize(user?.roleType);
  const normalizedPermissions = (Array.isArray(user?.permissions) ? user.permissions : [])
    .map((p) => normalize(typeof p === "string" ? p : p?.permission || p?.name || ""))
    .filter(Boolean);

  if (checking) {
    return null;
  }

  const hasPermission = (() => {
    if (!normalizedRequired) return false;
    if (normalizedPermissions.includes(normalizedRequired)) return true;

    if (normalizedRequired.includes(":")) {
      const basePermission = normalizedRequired.split(":")[0];
      if (normalizedPermissions.includes(basePermission)) return true;
      if (normalizedRoleType && (normalizedRoleType === basePermission || normalizedRoleType.includes(basePermission))) {
        return true;
      }

      const [requiredBase, requiredAction] = normalizedRequired.split(":");
      if (requiredAction === "read" && normalizedPermissions.includes(`${requiredBase}:write`)) {
        return true;
      }
      // Inventory has granular actions (issue/purchase/edit/history/analytics) instead of a
      // plain write - each of them implies read, same as write does elsewhere.
      if (
        requiredBase === "inventory" &&
        requiredAction === "read" &&
        ["issue", "purchase", "edit", "history", "analytics"].some((a) => normalizedPermissions.includes(`inventory:${a}`))
      ) {
        return true;
      }

      return false;
    }

    if (
      normalizedPermissions.includes(`${normalizedRequired}:read`) ||
      normalizedPermissions.includes(`${normalizedRequired}:write`)
    ) {
      return true;
    }
    if (normalizedRoleType && (normalizedRoleType === normalizedRequired || normalizedRoleType.includes(normalizedRequired))) {
      return true;
    }

    if (normalizedRequired === "settings" && normalizedPermissions.some((p) => p.startsWith("settings-"))) {
      return true;
    }

    return false;
  })();

  if (!hasPermission) {
    return (
      <Navigate
        to="/access-denied"
        state={{
          from: location.pathname,
          requiredPermission: permission,
          role,
          roleType: user?.roleType || null,
          permissions: normalizedPermissions,
        }}
        replace
      />
    );
  }

  return children;
}

export default RequirePermission;
